import React, { useEffect, useRef } from "react";
import AddIcon from "@mui/icons-material/Add";
import CircularProgress from "@mui/material/CircularProgress";
import { useTranslation } from 'react-i18next';
import { useNavigate } from "react-router-dom";
import { UserRoles } from "constants/userRoles";
import { RoutePaths } from "constants/RoutePaths";
import AuthGuard from "context/auth/AuthGuard";
import { LessonFormDrawer } from "components/views/editor/components/LessonFormDrawer";
import { NodeEditor } from "components/views/editor/components/NodeEditor";
import { GameEditor } from "components/views/editor/gameEditor/GameEditor";
import { ConfirmNavigationDialog } from "components/views/editor/components/ConfirmNavigationDialog";
import {
    LoadingBox,
    LessonEditorContentBox,
    ActionButtonsBox,
    EditorContainerBox,
    FormLoadingButton
} from "components/views/editor/AddTrainingPage.styles";
import { useTrainingPageData } from "hooks/useTrainingPageData";
import { useTrainingPageCallbacks } from "hooks/useTrainingPageCallbacks";
import { useEditorUnsavedChangesGuard } from "hooks/useEditorUnsavedChangesGuard";
import { useCreateLessonWithVersion } from "hooks/useCreateLessonWithVersion";
import { useGameEditorValidator } from "hooks/useGameEditorValidator";
import { LessonType } from "models/LessonType";

export const AddTrainingPage = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const {
        lessonFormData,
        setLessonFormData,
        editor,
        setEditor,
        gameEditorData,
        setGameEditorData,
        tags,
        tagsLoading,
        currentLanguage,
        isLessonFormValid,
        isNodeEditorValid,
        resetFormErrors,
        formValidationErrors,
        initializeEmptyForm
    } = useTrainingPageData();

    const { createLessonWithVersion, isCreating } = useCreateLessonWithVersion();
    const { isValid: isGameEditorValid } = useGameEditorValidator();

    const { handleFormDataChange, onEditorChange, onGameEditorChange } = useTrainingPageCallbacks({
        setLessonFormData,
        setEditor,
        setGameEditorData,
        formValidationErrors,
        resetFormErrors
    });

    const {
        isDialogOpen,
        handleCancelDialog,
        handleConfirmDialog,
        setBlockerActive
    } = useEditorUnsavedChangesGuard({
        editor: lessonFormData?.lessonType === LessonType.VR ? editor : undefined,
        savedConfig: undefined
    });

    const prevLessonTypeRef = useRef(lessonFormData?.lessonType);

    useEffect(() => {
        initializeEmptyForm();
    }, []);

    useEffect(() => {
        if (!lessonFormData) return;
        const prevType = prevLessonTypeRef.current;
        const currentType = lessonFormData.lessonType;
        prevLessonTypeRef.current = currentType;

        if (prevType !== currentType) {
            if (currentType === LessonType.GAME) {
                setEditor(null);
            } else {
                setGameEditorData({ fileId: null, launchParams: "", events: [] });
            }
        }
    }, [lessonFormData?.lessonType, setEditor, setGameEditorData]);

    const handleAddClick = async () => {
        if (!lessonFormData) return;
        if (!isLessonFormValid(lessonFormData)) return;

        if (lessonFormData.lessonType === LessonType.GAME) {
            if (!isGameEditorValid(gameEditorData)) return;
        } else {
            if (editor && editor.nodes.length > 0 && !isNodeEditorValid(editor)) return;
        }

        const newLessonId = await createLessonWithVersion(
            lessonFormData,
            currentLanguage,
            editor,
            lessonFormData.lessonType === LessonType.GAME ? gameEditorData : undefined
        );

        if (!newLessonId) return;

        editor?.storage?.saveTempSelectedGroup?.(newLessonId);

        setBlockerActive(false);
        navigate(`${RoutePaths.EDITOR}/${newLessonId}`);
    };

    if (!lessonFormData || !tags || tagsLoading) {
        return (
            <AuthGuard requiredRoles={[UserRoles.ADMIN, UserRoles.Patron]}>
                <EditorContainerBox>
                    <LoadingBox>
                        <CircularProgress color="primary"/>
                    </LoadingBox>
                </EditorContainerBox>
            </AuthGuard>
        );
    }

    const isGameLesson = lessonFormData.lessonType === LessonType.GAME;

    return (
        <AuthGuard requiredRoles={[UserRoles.ADMIN, UserRoles.Patron]}>
            <EditorContainerBox>
                <LessonEditorContentBox id="lesson-editor-content">
                    <LessonFormDrawer
                        tags={tags}
                        isDefaultOpen
                        lessonFormData={lessonFormData}
                        formValidationErrors={formValidationErrors}
                        onLessonDataChange={handleFormDataChange}
                        title={t("lessonEditor.createTraining")}
                    />
                    <ActionButtonsBox>
                        <FormLoadingButton
                            variant="contained"
                            loading={isCreating}
                            onClick={handleAddClick}
                            startIcon={<AddIcon/>}
                        >
                            {t("lessonEditor.add")}
                        </FormLoadingButton>
                    </ActionButtonsBox>
                    {isGameLesson ? (
                        <GameEditor
                            data={gameEditorData}
                            maxPoints={lessonFormData.maxPoints || 0}
                            onChange={onGameEditorChange}
                        />
                    ) : (
                        <NodeEditor onEditorChange={onEditorChange}/>
                    )}
                </LessonEditorContentBox>
                <ConfirmNavigationDialog
                    open={isDialogOpen}
                    handleCancel={handleCancelDialog}
                    handleConfirm={handleConfirmDialog}
                    message={t("nodeEditor.unsavedChanges")}
                />
            </EditorContainerBox>
        </AuthGuard>
    );
};